// The PDF render of a submission's report, over the web report it was opened
// from. Same endpoint as the page beneath it, asked with reportType `pdf` — the
// response is HTML again, not a binary, so this is another mount point for the
// server's markup rather than a document viewer.
//
// Presentational: the page owns the fetch and passes the three states down.
//
// Not a Modal: the shared primitive maxes out at size='lg' and puts its actions
// in a footer, while this is full-bleed with the actions in the top bar. The
// portal / Escape / focus-restore idioms are mirrored from Modal.tsx rather
// than reinvented, but the chrome is its own. Unlike Modal it does NOT close on
// a backdrop click — only the ✕ and Escape close it.
//
// Download PDF is live, and the page owns it too. It is a THIRD endpoint —
// submissions/download-pdf/:id/:language — returning a presigned link to a PDF
// the server has already built, so the app never generates or parses a file. The
// bytes are fetched and saved as a blob rather than linked to, because on a
// cross-origin link the `download` attribute is ignored and the bucket would
// decide between saving and previewing; see downloadFileUrl.
//
// Print is window.print() on this same on-screen render (user decision — no
// PDF library, iframe, or new tab). The open-effect stamps `report-pdf-open`
// on <body>, and report-view.scss's print block keys off it to scope output to
// the sheet and undo the fixed/overflow chrome so pages flow. Disabled while
// loading/error — there is no document to print.

import { useEffect, useId, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { Button, Icon, IconButton } from '@/components/common';
import { useCanExportReports } from '@/hooks/useModuleAccess';
import type { AppLanguage } from '@/redux/language/languageSlice';

interface ReportPdfOverlayProps {
  open: boolean;
  onClose: () => void;
  /** The server-rendered `pdf` document. */
  html?: string;
  /** The language `html` was rendered in — stamped on the sheet so an Arabic
   *  document doesn't read left-to-right inside an LTR shell. */
  lang?: AppLanguage;
  /** The document's page count, from the report response — the subtitle under
   *  the title. Hidden at 0/undefined (the design's "A4" half stays omitted:
   *  the page size has no source on this response). */
  totalPages?: number;
  loading?: boolean;
  error?: boolean;
  /** Mints the PDF's download link and saves the file. */
  onDownload: () => void;
  /** The link request or the file transfer is in flight — spins the button. */
  downloading?: boolean;
}

const ReportPdfOverlay = ({
  open,
  onClose,
  html,
  lang = 'en',
  totalPages,
  loading = false,
  error = false,
  onDownload,
  downloading = false,
}: ReportPdfOverlayProps) => {
  const { t } = useTranslation();
  const canExport = useCanExportReports();
  const titleId = useId();
  const panelRef = useRef<HTMLDivElement>(null);
  const restoreFocusRef = useRef<HTMLElement | null>(null);

  // Escape closes; focus moves into the panel on open and back on close. The
  // body class is the print scope — @media print hides everything outside it.
  useEffect(() => {
    if (!open) return;
    restoreFocusRef.current = document.activeElement as HTMLElement | null;
    panelRef.current?.focus();
    document.body.classList.add('report-pdf-open');

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.body.classList.remove('report-pdf-open');
      restoreFocusRef.current?.focus?.();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  if (!open) return null;

  return createPortal(
    <div className='report-pdf'>
      <header className='report-pdf__bar'>
        <span className='report-pdf__tile' aria-hidden='true'>
          <Icon name='file-text' size={20} />
        </span>
        <div className='report-pdf__heading'>
          <h2 id={titleId} className='report-pdf__title'>
            {t('report_view_pdf')}
          </h2>
          {totalPages !== undefined && totalPages > 0 && (
            <p className='report-pdf__subtitle'>{t('report_pdf_pages', { count: totalPages })}</p>
          )}
        </div>
        <div className='report-pdf__actions'>
          {/* Print and Download both produce an offline copy, so both ride the
              `reports` module's export extra (user decision) — the overlay
              gates itself since it portals from two independent parents. */}
          {canExport && (
            <>
              <Button
                variant='secondary'
                iconStart='printer'
                disabled={loading || error || !html}
                onClick={() => window.print()}
              >
                {t('report_pdf_print')}
              </Button>
              <Button
                variant='secondary'
                iconStart='download'
                loading={downloading}
                onClick={onDownload}
              >
                {t('report_pdf_download')}
              </Button>
            </>
          )}
          <IconButton icon='x' variant='ghost' aria-label={t('modal_close')} onClick={onClose} />
        </div>
      </header>

      {/* The backdrop is inert: closing is the ✕ or Escape, so a stray click
          beside a long document can't discard it. */}
      <div className='report-pdf__scroll'>
        <div
          ref={panelRef}
          className='report-pdf__sheet'
          role='dialog'
          aria-modal='true'
          aria-labelledby={titleId}
          tabIndex={-1}
        >
          {loading ? (
            <div className='report-pdf__status'>{t('text_loading')}</div>
          ) : error ? (
            <div className='report-pdf__status'>{t('error_generic')}</div>
          ) : (
            // As on the web report: the server owns everything below this line.
            <div
              lang={lang}
              dir={lang === 'ar' ? 'rtl' : 'ltr'}
              dangerouslySetInnerHTML={{ __html: html ?? '' }}
            />
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default ReportPdfOverlay;
